/**
 * Feb 2026 · User-Directive (3 Sep 2026) „Team-Notification im UI":
 * Poll-Hook fuer Angebots-Entscheidungen des Kunden. Analog zu
 * `handovers.js`: Wir pollen alle 30 s `GET /team/quote-events/mine`,
 * zeigen fuer jeden ungelesenen Eintrag einen Sonner-Toast an und
 * markieren ihn nach Interaktion als gelesen — sonst wuerde derselbe
 * Toast bei jeder Poll-Runde wieder aufpoppen.
 *
 * Der Toast unterscheidet visuell zwischen genehmigt (gruen) und
 * abgelehnt (rot) — deckungsgleich mit dem 4-Farben-Schema und der
 * E-Mail-Benachrichtigung. Klick auf „Ansehen" fuehrt in `/team/quotes`.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "./api";
import { useAuth } from "./auth";
import { deCurrency } from "./numberFormat";

const POLL_MS = 30000;

/**
 * „Gesehen"-Marker liegen serverseitig pro Nutzer (users.ui_prefs.seen_quote_events).
 * @param {{ enabled: boolean, onOpenQuotes: () => void }} opts
 */
export function useQuoteDecisionPolling({ enabled, onOpenQuotes }) {
  const { uiPrefs, setUiPrefs } = useAuth();
  const seenRef = useRef(null);
  if (seenRef.current === null) {
    seenRef.current = new Set(Array.isArray(uiPrefs?.seen_quote_events) ? uiPrefs.seen_quote_events : []);
  }
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const dismiss = async (id) => {
      try {
        await api.post(`/team/quote-events/${id}/dismiss`);
      } catch {
        /* silent — naechster Poll ueberspringt ihn nur, wenn read_at gesetzt ist */
      }
    };

    const showToast = (ev) => {
      const approved = ev.decision === "approved";
      const decider = ev.decider_name || "Kunde";
      const quoteTitle = ev.quote?.title || "Angebot";
      const parts = [];
      if (ev.quote?.number) parts.push(ev.quote.number);
      if (ev.property_name) parts.push(ev.property_name);
      if (ev.quote?.total) {
        try {
          parts.push(deCurrency(ev.quote.total, ev.quote?.currency || "EUR"));
        } catch { /* ignore */ }
      }
      const description = [quoteTitle, ...parts].join(" · ");
      const heading = approved
        ? `${decider} hat ein Angebot genehmigt`
        : `${decider} hat ein Angebot abgelehnt`;
      const noteBlock = ev.customer_note ? `\n„${ev.customer_note}"` : "";
      const fn = approved ? toast.success : toast.error;
      fn(heading, {
        description: description + noteBlock,
        duration: Infinity,
        id: `quote-decision-${ev.id}`,
        action: {
          label: "Ansehen",
          onClick: () => {
            dismiss(ev.id);
            onOpenQuotes?.();
          },
        },
        onDismiss: () => dismiss(ev.id),
        onAutoClose: () => dismiss(ev.id),
      });
    };

    const load = async () => {
      try {
        const { data } = await api.get("/team/quote-events/mine");
        if (cancelled || !Array.isArray(data)) return;
        let added = false;
        for (const ev of data) {
          if (!ev?.id || seenRef.current.has(ev.id)) continue;
          seenRef.current.add(ev.id);
          added = true;
          showToast(ev);
        }
        if (added) setUiPrefs({ seen_quote_events: Array.from(seenRef.current).slice(-300) });
      } catch {
        /* silent */
      }
    };

    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, onOpenQuotes, setUiPrefs]);
}
